import React from "react";

import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import HdIcon from "@mui/icons-material/Hd";
import LoopIcon from "@mui/icons-material/Loop";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import Grid3x3Icon from "@mui/icons-material/Grid3x3";
import SettingsOverscanIcon from "@mui/icons-material/SettingsOverscan";
import FlipIcon from "@mui/icons-material/Flip";
import StarIcon from "@mui/icons-material/Star";
import { WarningAmber } from "@mui/icons-material";

import { isMobile, isSafari } from "react-device-detect";
import { smpteToFrame } from "framerate-utils";

import DownloadMenu from "../Components/DownloadMenu";

import { MetaDataButtonIcon } from "../../../CustomIcons/CustomIcons";
import { nonselect } from "../../../utils/basicFunctions";

import { frameRateFromFio, normalizeTimecode, secondsToMedia } from "./playerUtils";

type PlayerControlsProps = {
	version: any;
	versionId: string;

	curPreviewAssetId: string;

	previewAssetIsVideo: boolean;
	previewAssetInfo: any;
	comparePreviewAssetInfo?: any;
	frameAccurateTimingAvailable: boolean;

	playerState: any;

	infoState: "none" | "compact" | "metadata";
	gridState: "none" | "grid" | "overscangrid";
	versionMetadata: any;

	desiredVideoQuality: number;
	filterSelects: boolean;

	overscanZoomEnabled: boolean;
	canToggleOverscanZoom: boolean;
	videoFlipped: boolean;
	canDownloadCurrentFrame?: boolean;
	isDownloadingFrame?: boolean;

	isSwap?: boolean;

	onToggleGridState: () => void;
	onToggleInfoState: (type?: "compact" | "metadata" | null) => void;
	onToggleFilterSelects: (evt: any) => void;
	onToggleQuality: () => void;
	onToggleOverscanZoom: () => void;
	onToggleVideoFlip: () => void;
	onDownloadCurrentFrame?: (format: "png" | "jpeg") => void | Promise<void>;
};

type TimecodeRange = {
	fps: number;
	frames: number;
	startTimecode: string;
	startFrame: number;
	endFrame: number;
	frameRate: any;
};

type TimelineOwner = "primary" | "compare";

type ComparisonTimelineConfig = {
	scrubOwner: TimelineOwner;
	barOwner: TimelineOwner | null;
	barColor: "primary" | "secondary" | null;
	barLeftPercent: number;
	barWidthPercent: number;
	primaryRange: TimecodeRange;
	compareRange: TimecodeRange;
};

function parsePositiveNumber(value: any): number {
	const parsed = Number(value);

	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function clampNumber(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

function numbersAlmostEqual(a: number, b: number, threshold = 0.001) {
	return Math.abs(a - b) <= threshold;
}

function getRawAssetStartTimecode(assetInfo: any): string | null {
	const rawTimecode = assetInfo?.metadata?.timecode ?? assetInfo?.timecode;

	if (rawTimecode === undefined || rawTimecode === null) {
		return null;
	}

	const trimmedTimecode = String(rawTimecode).trim();

	if (!trimmedTimecode.length) {
		return null;
	}

	return trimmedTimecode;
}

function getAssetStartTimecode(assetInfo: any): string | null {
	const rawTimecode = getRawAssetStartTimecode(assetInfo);

	if (!rawTimecode) {
		return null;
	}

	const normalizedTimecode = normalizeTimecode(rawTimecode);

	if (typeof normalizedTimecode !== "string" || !normalizedTimecode.trim().length) {
		return null;
	}

	return normalizedTimecode;
}

function getAssetTimecodeRange(assetInfo: any): TimecodeRange | null {
	const fps = parsePositiveNumber(assetInfo?.fps);
	const frames = parsePositiveNumber(assetInfo?.frames);

	if (fps <= 0 || frames <= 0) {
		return null;
	}

	const frameRate = frameRateFromFio(assetInfo?.fps);

	if (typeof frameRate === "undefined") {
		return null;
	}

	const startTimecode = getAssetStartTimecode(assetInfo);

	if (!startTimecode) {
		return null;
	}

	try {
		const startFrame = smpteToFrame(frameRate, startTimecode);

		if (!Number.isFinite(startFrame)) {
			return null;
		}

		return {
			fps,
			frames,
			startTimecode,
			startFrame,
			endFrame: startFrame + frames - 1,
			frameRate,
		};
	} catch {
		return null;
	}
}

function getRangeLength(range: TimecodeRange) {
	return Math.max(1, range.endFrame - range.startFrame + 1);
}

function rangesOverlap(a: TimecodeRange, b: TimecodeRange) {
	return Math.max(a.startFrame, b.startFrame) <= Math.min(a.endFrame, b.endFrame);
}

function rangesHaveSameArea(a: TimecodeRange, b: TimecodeRange) {
	return a.startFrame === b.startFrame && a.endFrame === b.endFrame;
}

function getAssetTimelineLength(assetInfo: any) {
	const frames = parsePositiveNumber(assetInfo?.frames ?? assetInfo?.metadata?.frames ?? assetInfo?.metadata?.blob?.frames);

	if (frames > 0) {
		return frames;
	}

	const duration = parsePositiveNumber(assetInfo?.duration ?? assetInfo?.metadata?.duration ?? assetInfo?.metadata?.blob?.duration);
	const fps = parsePositiveNumber(assetInfo?.fps ?? assetInfo?.metadata?.fps ?? assetInfo?.metadata?.blob?.fps);

	if (duration > 0 && fps > 0) {
		return duration * fps;
	}

	return duration;
}

function getBarPositionOnScrubRange(scrubRange: TimecodeRange, barRange: TimecodeRange) {
	const scrubLength = getRangeLength(scrubRange);

	const rawLeftPercent = ((barRange.startFrame - scrubRange.startFrame) / scrubLength) * 100;
	const rawRightPercent = ((barRange.endFrame - scrubRange.startFrame + 1) / scrubLength) * 100;

	const visibleLeftPercent = clampNumber(rawLeftPercent, 0, 100);
	const visibleRightPercent = clampNumber(rawRightPercent, 0, 100);
	const visibleWidthPercent = visibleRightPercent - visibleLeftPercent;

	return {
		leftPercent: visibleLeftPercent,
		widthPercent: visibleWidthPercent,
		visible: visibleWidthPercent > 0,
	};
}

function getComparisonTimelineConfig(primaryAssetInfo: any, compareAssetInfo: any): ComparisonTimelineConfig | null {
	const primaryRange = getAssetTimecodeRange(primaryAssetInfo);
	const compareRange = getAssetTimecodeRange(compareAssetInfo);

	if (!primaryRange || !compareRange) {
		return null;
	}

	if (!numbersAlmostEqual(primaryRange.fps, compareRange.fps)) {
		return null;
	}

	if (!rangesOverlap(primaryRange, compareRange)) {
		return null;
	}

	if (rangesHaveSameArea(primaryRange, compareRange)) {
		return {
			scrubOwner: "primary",
			barOwner: null,
			barColor: null,
			barLeftPercent: 0,
			barWidthPercent: 0,
			primaryRange,
			compareRange,
		};
	}

	const primaryLength = getRangeLength(primaryRange);
	const compareLength = getRangeLength(compareRange);

	const scrubOwner: TimelineOwner = compareLength > primaryLength ? "compare" : "primary";
	const barOwner: TimelineOwner = scrubOwner === "primary" ? "compare" : "primary";
	const scrubRange = scrubOwner === "primary" ? primaryRange : compareRange;
	const barRange = barOwner === "primary" ? primaryRange : compareRange;
	const barPosition = getBarPositionOnScrubRange(scrubRange, barRange);

	if (!barPosition.visible) {
		return null;
	}

	return {
		scrubOwner,
		barOwner,
		barColor: barOwner === "primary" ? "primary" : "secondary",
		barLeftPercent: barPosition.leftPercent,
		barWidthPercent: barPosition.widthPercent,
		primaryRange,
		compareRange,
	};
}

function getCompareHasLongerTimeline(primaryAssetInfo: any, compareAssetInfo: any, comparisonTimelineConfig: ComparisonTimelineConfig | null) {
	if (!compareAssetInfo) {
		return false;
	}

	if (comparisonTimelineConfig) {
		return getRangeLength(comparisonTimelineConfig.compareRange) > getRangeLength(comparisonTimelineConfig.primaryRange);
	}

	const primaryLength = getAssetTimelineLength(primaryAssetInfo);
	const compareLength = getAssetTimelineLength(compareAssetInfo);

	return compareLength > primaryLength;
}

function getFrameAccurateSliderValueFromCurrentFrame(currentFrame: number, timelineRange: TimecodeRange, primaryRange: TimecodeRange) {
	const primaryAbsoluteFrame = primaryRange.startFrame + currentFrame;
	const timelineLocalFrame = primaryAbsoluteFrame - timelineRange.startFrame;
	const safariOffset = isSafari ? 1 : 0;

	return clampNumber(timelineLocalFrame + safariOffset, safariOffset, Math.max(safariOffset, timelineRange.frames));
}

function getMappedPrimarySeekValueFromTimelineValue(timelineValueInput: any, timelineRange: TimecodeRange, primaryRange: TimecodeRange) {
	const timelineValue = Array.isArray(timelineValueInput) ? Number(timelineValueInput[0]) : Number(timelineValueInput);
	const safeTimelineValue = Number.isFinite(timelineValue) ? timelineValue : 0;
	const safariOffset = isSafari ? 1 : 0;

	const timelineLocalFrame = clampNumber(Math.floor(safeTimelineValue - safariOffset), 0, Math.max(0, timelineRange.frames - 1));
	const absoluteFrame = timelineRange.startFrame + timelineLocalFrame;
	const primaryLocalFrame = absoluteFrame - primaryRange.startFrame;

	return clampNumber(primaryLocalFrame + safariOffset, safariOffset, Math.max(safariOffset, primaryRange.frames));
}

function formatLoopPoint(value: number | null | undefined) {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return "--:--";
	}

	return secondsToMedia(Math.max(0, value));
}

function getFrameAccurateTimelineValueFromSeconds(timeSeconds: number, timelineRange: TimecodeRange, primaryRange: TimecodeRange, primaryAssetInfo: any) {
	const fps = parsePositiveNumber(primaryAssetInfo?.fps);
	const safariOffset = isSafari ? 1 : 0;

	if (fps <= 0) {
		return safariOffset;
	}

	const primaryLocalFrame = clampNumber(Math.floor(Math.max(0, timeSeconds) * fps), 0, Math.max(0, primaryRange.frames - 1));
	const primaryAbsoluteFrame = primaryRange.startFrame + primaryLocalFrame;
	const timelineLocalFrame = primaryAbsoluteFrame - timelineRange.startFrame;

	return clampNumber(timelineLocalFrame + safariOffset, safariOffset, Math.max(safariOffset, timelineRange.frames));
}

function PlayerControlsComponent({
	version,
	versionId,

	curPreviewAssetId,

	previewAssetIsVideo,
	previewAssetInfo,
	comparePreviewAssetInfo,
	frameAccurateTimingAvailable,

	playerState,

	infoState,
	gridState,
	versionMetadata,

	desiredVideoQuality,
	filterSelects,

	overscanZoomEnabled,
	canToggleOverscanZoom,
	videoFlipped,
	canDownloadCurrentFrame = false,
	isDownloadingFrame = false,

	isSwap = false,

	onToggleGridState,
	onToggleInfoState,
	onToggleFilterSelects,
	onToggleQuality,
	onToggleOverscanZoom,
	onToggleVideoFlip,
	onDownloadCurrentFrame,
}: PlayerControlsProps) {
	const {
		playing,

		prefPlaying,
		progress,

		looping,
		loopInPoint,
		loopOutPoint,
		loopRange,
		loopRangeActive,
		setLoopInToCurrentTime,
		setLoopOutToCurrentTime,
		clearLoopPoints,
		muted,

		fioTimingInvalid,
		duration,
		curFrame,
		showTC,

		toggleShowTC,
		handlePause,
		handleTogglePlayPause,
		handleSeek,
		handleSeekStop,
		handleToggleMute,
		handleToggleLoop,
		handleClickFullscreen,
		getCurMedia,
		getLastMedia,
		getCurTC,
	} = playerState;

	const totalFrames = Math.max(1, previewAssetInfo?.frames || 1);
	const frameDigits = (totalFrames - 1).toString().length;

	const comparisonTimelineConfig = React.useMemo(() => {
		if (!previewAssetIsVideo || !frameAccurateTimingAvailable || fioTimingInvalid) {
			return null;
		}

		return getComparisonTimelineConfig(previewAssetInfo, comparePreviewAssetInfo);
	}, [previewAssetIsVideo, frameAccurateTimingAvailable, fioTimingInvalid, previewAssetInfo, comparePreviewAssetInfo]);

	const compareHasLongerTimeline = React.useMemo(() => {
		return getCompareHasLongerTimeline(previewAssetInfo, comparePreviewAssetInfo, comparisonTimelineConfig);
	}, [previewAssetInfo, comparePreviewAssetInfo, comparisonTimelineConfig]);

	const timelineSliderColor: "primary" | "secondary" = isSwap || compareHasLongerTimeline ? "secondary" : "primary";

	const hasLoopInPoint = typeof loopInPoint === "number" && Number.isFinite(loopInPoint);
	const hasLoopOutPoint = typeof loopOutPoint === "number" && Number.isFinite(loopOutPoint);
	const hasLoopPoints = hasLoopInPoint || hasLoopOutPoint;
	const loopStatusLabel = loopRangeActive && loopRange?.in !== null && loopRange?.out !== null ? `Looping ${formatLoopPoint(loopRange.in)} - ${formatLoopPoint(loopRange.out)}` : hasLoopPoints ? `Loop points: ${formatLoopPoint(loopInPoint)} - ${formatLoopPoint(loopOutPoint)}` : "No loop points set";

	const scrubRange = comparisonTimelineConfig?.scrubOwner === "compare" ? comparisonTimelineConfig.compareRange : comparisonTimelineConfig?.primaryRange ?? getAssetTimecodeRange(previewAssetInfo);

	const shouldUseComparisonTimeline = Boolean(comparisonTimelineConfig && scrubRange && frameAccurateTimingAvailable && !fioTimingInvalid);

	const sliderMin = fioTimingInvalid || !frameAccurateTimingAvailable ? 0 : isSafari ? 1 : 0;
	const sliderMax = fioTimingInvalid || !frameAccurateTimingAvailable ? Math.max(duration || 0.01, 0.01) : shouldUseComparisonTimeline && scrubRange ? scrubRange.frames : previewAssetInfo?.frames || 1;

	const sliderValue =
		fioTimingInvalid || !frameAccurateTimingAvailable
			? (duration ?? 0) * (progress?.played ?? 0)
			: shouldUseComparisonTimeline && scrubRange && comparisonTimelineConfig
			? getFrameAccurateSliderValueFromCurrentFrame(curFrame, comparisonTimelineConfig.scrubOwner === "compare" ? comparisonTimelineConfig.compareRange : comparisonTimelineConfig.primaryRange, comparisonTimelineConfig.primaryRange)
			: Math.floor((progress?.played ?? 0) * (previewAssetInfo?.frames ?? 0)) + (isSafari ? 1 : 0);

	const loopPointSliderPosition = React.useMemo(() => {
		if (!previewAssetIsVideo || sliderMax <= sliderMin) {
			return null;
		}

		const toSliderValue = (pointSeconds: number | null | undefined) => {
			if (typeof pointSeconds !== "number" || !Number.isFinite(pointSeconds)) {
				return null;
			}

			if (fioTimingInvalid || !frameAccurateTimingAvailable) {
				return clampNumber(pointSeconds, sliderMin, sliderMax);
			}

			const primaryRange = comparisonTimelineConfig?.primaryRange ?? getAssetTimecodeRange(previewAssetInfo);

			if (!primaryRange) {
				return null;
			}

			const timelineRange = shouldUseComparisonTimeline && scrubRange ? scrubRange : primaryRange;

			return getFrameAccurateTimelineValueFromSeconds(pointSeconds, timelineRange, primaryRange, previewAssetInfo);
		};

		const inValue = toSliderValue(loopInPoint);
		const outValue = toSliderValue(loopOutPoint);
		const toPercent = (value: number | null) => {
			if (value === null) {
				return null;
			}

			return clampNumber(((value - sliderMin) / (sliderMax - sliderMin)) * 100, 0, 100);
		};

		const inPercent = toPercent(inValue);
		const outPercent = toPercent(outValue);

		const sliderPercent = toPercent(Number.isFinite(Number(sliderValue)) ? Number(sliderValue) : null);
		const rangeLeftPercent = inPercent !== null && outPercent !== null ? Math.min(inPercent, outPercent) : null;
		const rangeRightPercent = inPercent !== null && outPercent !== null ? Math.max(inPercent, outPercent) : null;
		const rangeWidthPercent = rangeLeftPercent !== null && rangeRightPercent !== null ? rangeRightPercent - rangeLeftPercent : null;
		const loopFillCurrentPercent = loopRangeActive && rangeLeftPercent !== null && rangeRightPercent !== null && sliderPercent !== null ? clampNumber(sliderPercent, rangeLeftPercent, rangeRightPercent) : null;

		return {
			inPercent,
			outPercent,
			rangeLeftPercent,
			rangeWidthPercent,
			loopFillLeftPercent: loopRangeActive ? rangeLeftPercent : null,
			loopFillWidthPercent: loopFillCurrentPercent !== null && rangeLeftPercent !== null ? Math.max(0, loopFillCurrentPercent - rangeLeftPercent) : null,
		};
	}, [comparisonTimelineConfig, fioTimingInvalid, frameAccurateTimingAvailable, loopInPoint, loopOutPoint, loopRangeActive, previewAssetInfo, previewAssetIsVideo, scrubRange, shouldUseComparisonTimeline, sliderMax, sliderMin, sliderValue]);

	const handleTimelineSeek = React.useCallback(
		(event: any, newValue: any | number[]) => {
			if (!comparisonTimelineConfig || !scrubRange || comparisonTimelineConfig.scrubOwner === "primary" || fioTimingInvalid || !frameAccurateTimingAvailable) {
				handleSeek(event, newValue);
				return;
			}

			const mappedPrimarySeekValue = getMappedPrimarySeekValueFromTimelineValue(newValue, scrubRange, comparisonTimelineConfig.primaryRange);

			handleSeek(event, mappedPrimarySeekValue);
		},
		[comparisonTimelineConfig, scrubRange, fioTimingInvalid, frameAccurateTimingAvailable, handleSeek]
	);

	const handleTimelineSeekStop = React.useCallback(
		(event: any) => {
			handleSeekStop(event);
		},
		[handleSeekStop]
	);

	const handleSetLoopIn = React.useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			setLoopInToCurrentTime?.();
		},
		[setLoopInToCurrentTime]
	);

	const handleSetLoopOut = React.useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			setLoopOutToCurrentTime?.();
		},
		[setLoopOutToCurrentTime]
	);

	const handleClearLoopPoints = React.useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			clearLoopPoints?.();
		},
		[clearLoopPoints]
	);

	const renderTimeDisplayContent = (keyPrefix: string) => {
		if (!previewAssetIsVideo) {
			return null;
		}

		if (showTC === 0 || fioTimingInvalid || !frameAccurateTimingAvailable) {
			return (
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Box sx={{ display: "flex", mr: 0.5 }}>
						{getCurMedia()
							?.split(":")
							?.map((t: string, index: number) => {
								return (
									<Box key={`${keyPrefix}_tc_0_${versionId}_${index}`} sx={{ display: "flex" }}>
										{index !== 0 ? <Box sx={{ width: 6, mr: 0.1, ml: 0.1 }}>:</Box> : null}
										<Box sx={{ width: 18, textAlign: "left" }}>{t}</Box>
									</Box>
								);
							})}
					</Box>

					<Box sx={{ width: 8, opacity: 0.5 }}>/</Box>

					<Box sx={{ display: "flex", opacity: 0.5, ml: 0.35 }}>
						{getLastMedia()
							?.split(":")
							?.map((t: string, index: number) => {
								return (
									<Box key={`${keyPrefix}_tc_00_${versionId}_${index}`} sx={{ display: "flex" }}>
										{index !== 0 ? <Box sx={{ width: 6, mr: 0.1, ml: 0.1 }}>:</Box> : null}
										<Box sx={{ width: 18, textAlign: "left" }}>{t}</Box>
									</Box>
								);
							})}
					</Box>
				</Box>
			);
		}

		if (showTC === 1) {
			return (
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Box sx={{ textAlign: "left", width: frameDigits * 10 }}>{curFrame.toString().padStart(frameDigits, "0")}</Box>
					<Box sx={{ width: 8, opacity: 0.5 }}>/</Box>
					<Box sx={{ pl: 0.25, width: frameDigits * 10, opacity: 0.5 }}>{totalFrames - 1}</Box>
				</Box>
			);
		}

		if (showTC === 2) {
			return (
				<Box sx={{ display: "flex", alignItems: "center" }}>
					{getCurTC()
						?.split(":")
						?.map((t: string, index: number) => {
							return (
								<Box key={`${keyPrefix}_tc_2_${versionId}_${index}`} sx={{ display: "flex" }}>
									{index !== 0 ? <Box sx={{ width: 6, mr: 0.25 }}>:</Box> : null}
									<Box sx={{ width: 20, textAlign: "left" }}>{t}</Box>
								</Box>
							);
						})}
				</Box>
			);
		}

		return null;
	};

	const renderTimeDisplayButton = (keyPrefix: string, mobile = false) => {
		if (!previewAssetIsVideo) {
			return null;
		}

		const button = (
			<Button
				color="info"
				sx={{
					color: "white",
					height: mobile ? "32px" : "34px",
					minHeight: mobile ? "32px" : "34px",
					px: 1,
					whiteSpace: "nowrap",
					pointerEvents: "auto",
					backgroundColor: mobile ? "rgba(0,0,0,0.45)" : "transparent",
					borderRadius: mobile ? "6px" : 0,
					"&:hover": {
						backgroundColor: mobile ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.04)",
					},
				}}
				onClick={(evt) => {
					evt.preventDefault();
					evt.stopPropagation();
					toggleShowTC();
				}}
			>
				{(fioTimingInvalid || !frameAccurateTimingAvailable) && <WarningAmber fontSize="inherit" />}
				{renderTimeDisplayContent(keyPrefix)}
			</Button>
		);

		if (mobile) {
			return button;
		}

		return (
			<Tooltip title={fioTimingInvalid || !frameAccurateTimingAvailable ? "No frame accurate timing available for this asset." : "Toggle time display"}>
				<Box sx={{ height: "34px", overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center", pointerEvents: "auto" }}>{button}</Box>
			</Tooltip>
		);
	};

	return (
		<Box
			sx={{
				...nonselect,
				position: "absolute",
				top: 51,
				left: 0,
				width: "100%",
				height: "calc(100% - 203px)",
				zIndex: 40,
				display: "flex",
				flexDirection: "column",
				justifyContent: "flex-end",
				boxSizing: "border-box",
				pointerEvents: "none",
			}}
		>
			<Box
				sx={{
					display: { xs: previewAssetIsVideo ? "flex" : "none", sm: "none" },
					position: "absolute",
					left: 0,
					bottom: "46px",
					width: "100%",
					height: "34px",
					alignItems: "center",
					justifyContent: "center",
					zIndex: 60,
					pointerEvents: "none",
					boxSizing: "border-box",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						pointerEvents: "auto",
					}}
				>
					{renderTimeDisplayButton("mobile", true)}
				</Box>
			</Box>

			<Box
				sx={{
					width: "100%",
					height: "40px",
					display: "flex",
					flexDirection: "column",
					boxSizing: "border-box",
					pointerEvents: "auto",
					position: "relative",
				}}
			>
				{comparisonTimelineConfig?.barOwner && comparisonTimelineConfig.barColor ? (
					<Box
						sx={{
							position: "absolute",
							left: `${comparisonTimelineConfig.barLeftPercent}%`,
							width: `${comparisonTimelineConfig.barWidthPercent}%`,
							top: 6,
							height: 4,
							transform: "translateY(-50%)",
							borderRadius: 999,
							backgroundColor: `white`,
							pointerEvents: "none",
							zIndex: 51,
						}}
					/>
				) : null}

				{loopPointSliderPosition?.rangeLeftPercent != null && loopPointSliderPosition?.rangeWidthPercent != null ? (
					<Box
						sx={{
						position: "absolute",
						left: `${loopPointSliderPosition.rangeLeftPercent}%`,
						width: `${loopPointSliderPosition.rangeWidthPercent}%`,
						top: 6,
						height: 4,
						transform: "translateY(-50%)",
						borderRadius: 999,
						backgroundColor: "rgba(255,255,255,0.45)",
						pointerEvents: "none",
						zIndex: 52,
					}}
					/>
				) : null}

				{loopPointSliderPosition?.loopFillLeftPercent != null && loopPointSliderPosition?.loopFillWidthPercent != null ? (
					<Box
						sx={{
							position: "absolute",
							left: `${loopPointSliderPosition.loopFillLeftPercent}%`,
							width: `${loopPointSliderPosition.loopFillWidthPercent}%`,
							top: 6,
							height: 4,
							transform: "translateY(-50%)",
							borderRadius: 999,
							backgroundColor: `${timelineSliderColor}.main`,
							pointerEvents: "none",
							zIndex: 53,
						}}
					/>
				) : null}

				{loopPointSliderPosition?.inPercent != null ? (
					<Box
						sx={{
						position: "absolute",
						left: `${loopPointSliderPosition.inPercent}%`,
						top: 6,
						width: 3,
						height: 16,
						transform: "translate(-50%, -50%)",
						borderRadius: 999,
						backgroundColor: "success.main",
						pointerEvents: "none",
						zIndex: 54,
					}}
					/>
				) : null}

				{loopPointSliderPosition?.outPercent != null ? (
					<Box
						sx={{
						position: "absolute",
						left: `${loopPointSliderPosition.outPercent}%`,
						top: 6,
						width: 3,
						height: 16,
						transform: "translate(-50%, -50%)",
						borderRadius: 999,
						backgroundColor: "error.main",
						pointerEvents: "none",
						zIndex: 54,
					}}
					/>
				) : null}

				<Slider
					size="small"
					step={fioTimingInvalid || !frameAccurateTimingAvailable ? 0.01 : 1}
					sx={{
						opacity: previewAssetIsVideo ? 1 : 0,
						borderRadius: 0,
						height: "12px",
						p: 0,
						pointerEvents: previewAssetIsVideo ? "auto" : "none",
						boxSizing: "border-box",
						"& .MuiSlider-rail": {
							opacity: 0.28,
						},
						"& .MuiSlider-track": {
							transition: "none",
						},
						"& .MuiSlider-thumb": {
							transition: "none",
							height: 12,
							width: 2,
							borderRadius: 0,
							backgroundColor: "#fff",
							boxShadow: "none!important",
							"&focused, &activated, &jumped, &:hover": {
								boxShadow: "none!important",
							},
						},
					}}
					aria-label="Timeline"
					min={sliderMin}
					max={sliderMax}
					value={sliderValue}
					color={timelineSliderColor}
					track={loopRangeActive ? false : "normal"}
					onChange={handleTimelineSeek}
					onChangeCommitted={handleTimelineSeekStop}
				/>

				<Box
					sx={{
						backgroundColor: "background.paper",
						width: "100%",
						height: "34px",
						p: 0,
						mt: { sm: "0", xs: isMobile ? "-17px" : "0" },
						display: "flex",
						zIndex: 50,
						pointerEvents: "auto",
						boxSizing: "border-box",
					}}
				>
					<Box sx={{ display: previewAssetIsVideo ? "flex" : "none", flex: "1 1 0px", minWidth: 0 }}>
						<IconButton sx={{ ml: 1 }} aria-label="play/pause" disableRipple size="small" onClick={handleTogglePlayPause}>
							{playing.current || prefPlaying ? <PauseIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}
						</IconButton>

						<Tooltip title={loopStatusLabel}>
							<IconButton color={looping ? timelineSliderColor : undefined} aria-label="loop" disableRipple size="small" onClick={handleToggleLoop}>
								<LoopIcon fontSize="inherit" />
							</IconButton>
						</Tooltip>

						<Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: 0.25 }}>
							<Tooltip title={`Set loop in point (Shift+I)${hasLoopInPoint ? ` (${formatLoopPoint(loopInPoint)})` : ""}`}>
								<Button
									aria-label="set loop in point"
									color={hasLoopInPoint ? timelineSliderColor : "inherit"}
									disableRipple
									size="small"
									onClick={handleSetLoopIn}
									sx={{ minWidth: 28, height: 24, minHeight: 24, px: 0.5, fontSize: 11, lineHeight: 1, pointerEvents: "auto" }}
								>
									In
								</Button>
							</Tooltip>

							<Tooltip title={`Set loop out point (Shift+O)${hasLoopOutPoint ? ` (${formatLoopPoint(loopOutPoint)})` : ""}`}>
								<Button
									aria-label="set loop out point"
									color={hasLoopOutPoint ? timelineSliderColor : "inherit"}
									disableRipple
									size="small"
									onClick={handleSetLoopOut}
									sx={{ minWidth: 32, height: 24, minHeight: 24, px: 0.5, fontSize: 11, lineHeight: 1, pointerEvents: "auto" }}
								>
									Out
								</Button>
							</Tooltip>

							<Tooltip title="Clear loop points (Shift+G)">
								<Box component="span" sx={{ display: "inline-flex" }}>
									<Button
										aria-label="clear loop points"
										color="inherit"
										disableRipple
										disabled={!hasLoopPoints}
										size="small"
										onClick={handleClearLoopPoints}
										sx={{ minWidth: 38, height: 24, minHeight: 24, px: 0.5, fontSize: 11, lineHeight: 1, pointerEvents: "auto" }}
									>
										Clear
									</Button>
								</Box>
							</Tooltip>
						</Box>

						{previewAssetInfo?.metadata?.blob?.has_audio || false ? (
							<IconButton aria-label="muted" disableRipple size="small" onClick={handleToggleMute}>
								{muted ? <VolumeOffIcon fontSize="inherit" /> : <VolumeUpIcon fontSize="inherit" />}
							</IconButton>
						) : null}

						<IconButton aria-label="overlay" disableRipple size="small" color={gridState !== "none" ? timelineSliderColor : undefined} onClick={onToggleGridState}>
							<Grid3x3Icon fontSize="inherit" />
						</IconButton>

						<Tooltip title={canToggleOverscanZoom ? "Toggle overscan zoom" : "No overscan available"}>
							<Box component="span" sx={{ display: "inline-flex" }}>
								<IconButton aria-label="overscan zoom" disableRipple size="small" color={overscanZoomEnabled ? "secondary" : undefined} onClick={onToggleOverscanZoom} disabled={!canToggleOverscanZoom}>
									<SettingsOverscanIcon fontSize="inherit" />
								</IconButton>
							</Box>
						</Tooltip>

						<Tooltip title={videoFlipped ? "Disable horizontal flip" : "Flip video horizontally"}>
							<Box component="span" sx={{ display: "inline-flex" }}>
								<IconButton aria-label="flip video horizontally" disableRipple size="small" color={videoFlipped ? timelineSliderColor : undefined} onClick={onToggleVideoFlip}>
									<FlipIcon fontSize="inherit" />
								</IconButton>
							</Box>
						</Tooltip>

						<IconButton aria-label="metadataviewer" disableRipple size="small" color={infoState !== "none" && versionMetadata ? timelineSliderColor : undefined} onClick={() => onToggleInfoState()} disabled={!versionMetadata}>
							<MetaDataButtonIcon fontSize="inherit" />
						</IconButton>
					</Box>

					<Box sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "center", alignItems: "center", pointerEvents: "auto" }}>{renderTimeDisplayButton("desktop")}</Box>

					<Box sx={{ display: "flex", alignItems: "center", flex: "1 1 0px", minWidth: 0 }}>
						<Box sx={{ display: "flex", ml: "auto", mr: 1 }}>
							<IconButton aria-label="favorite" disableRipple size="small" onClick={onToggleFilterSelects}>
								{filterSelects ? <StarIcon color={"primary"} fontSize="inherit" /> : <StarIcon sx={{ opacity: 0.25 }} fontSize="inherit" />}
							</IconButton>

							<DownloadMenu curFioAssetID={curPreviewAssetId} onClick={handlePause} version={version} canDownloadCurrentFrame={canDownloadCurrentFrame} isDownloadingFrame={isDownloadingFrame} onDownloadCurrentFrame={onDownloadCurrentFrame} />

							{previewAssetIsVideo ? (
								<>
									<IconButton color={desiredVideoQuality === 0 || desiredVideoQuality === 2160 ? timelineSliderColor : undefined} aria-label="quality" disableRipple size="small" onClick={onToggleQuality}>
										<HdIcon fontSize="inherit" />
									</IconButton>

									<IconButton aria-label="fullscreen" disableRipple size="small" onClick={handleClickFullscreen}>
										<FullscreenIcon fontSize="inherit" />
									</IconButton>
								</>
							) : null}
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

export const PlayerControls = React.memo(PlayerControlsComponent);
